import { useAppModal } from "@/context/AppModalContext";
import StoreBadges from "@/components/site/StoreBadges";
import { SITE } from "@/constants/testIds";

/**
 * Shared building blocks for content pages.
 * PageIntro  , kicker + h1 + optional lede + primary CTAs.
 * PageBadgeBand, mid-page store badges (satisfies the "badges on every page" rule).
 * PageFinalCta, bottom store-badges CTA.
 */

export function PageIntro({ kicker, title, lede, children }) {
  const { openModal } = useAppModal();
  return (
    <section className="pt-14 sm:pt-20 lg:pt-24 pb-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            {kicker}
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-[52px] font-bold tracking-tight leading-[1.05] text-[color:var(--yn-ink)]">
            {title}
          </h1>
          {lede && (
            <p className="mt-6 text-lg sm:text-xl leading-relaxed text-[color:var(--yn-muted)]">
              {lede}
            </p>
          )}
          {children && <div className="mt-8">{children}</div>}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <StoreBadges
              placement={`page-${kicker.toLowerCase().replace(/\s+/g, "-")}-hero`}
              appTestId={SITE.heroAppStoreBadge}
              playTestId={SITE.heroPlayStoreBadge}
            />
            <button
              type="button"
              onClick={() => openModal(`page-${kicker}`)}
              className="text-sm font-medium text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
            >
              Open the install panel →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PageBadgeBand({ heading = "Install it before you need it." }) {
  return (
    <section
      className="border-y border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)]"
      data-testid={SITE.pageBadgeBand}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[color:var(--yn-ink)] tracking-tight">
            {heading}
          </h2>
          <div className="md:justify-self-end">
            <StoreBadges
              placement="page-mid-band"
              appTestId={SITE.midAppStoreBadge}
              playTestId={SITE.midPlayStoreBadge}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export function Section({ children, className = "", ...rest }) {
  return (
    <section className={`py-14 md:py-20 ${className}`} {...rest}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

export function Prose({ children }) {
  return (
    <div className="prose-yn max-w-3xl text-[color:var(--yn-muted)] text-[16px] sm:text-[17px] leading-[1.75]">
      {children}
    </div>
  );
}
